<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import {
    type DurationMode,
    formatDuration,
    nextDurationMode,
} from "../composables/useDuration";

const props = withDefaults(
    defineProps<{
        modelValue: number | null | undefined;
        defaultMode?: DurationMode;
        interactive?: boolean;
    }>(),
    {
        defaultMode: "hhmm",
        interactive: true,
    },
);

const { t } = useI18n();

const mode = ref<DurationMode>(props.defaultMode);

const formatted = computed(() => {
    const v = props.modelValue;
    if (v === null || v === undefined || Number.isNaN(v)) {
        return "";
    }
    const total = Math.max(0, Math.round(v));
    if (mode.value === "minutes") {
        return t("crud.duration.minutes", { count: total }, total);
    }
    if (mode.value === "decimal") {
        const hours = Math.round((total / 60) * 100) / 100;
        const choice = hours === 1 ? 1 : 2;
        return t("crud.duration.hours", { count: hours }, choice);
    }
    return formatDuration(v, "hhmm");
});

function cycleMode() {
    if (!props.interactive) {
        return;
    }
    mode.value = nextDurationMode(mode.value);
}
</script>

<template>
    <span
        v-if="interactive"
        role="button"
        tabindex="0"
        class="hover:text-foreground text-muted-foreground cursor-pointer tabular-nums select-none"
        :title="`Switch display mode`"
        @click="cycleMode"
        @keydown.enter.prevent="cycleMode"
        @keydown.space.prevent="cycleMode"
    >
        {{ formatted || "—" }}
    </span>
    <span v-else class="tabular-nums">{{ formatted || "—" }}</span>
</template>
